import { useAuth } from '@hotpot-arcade/fanforge-auth';
import { UnauthenticatedPage } from '../pages/unauthenticated-page';
import { LoadingPage } from '../pages/loading-page';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { GamePage } from '../pages/game-page';
import { RecognitionDoseProvider } from '@hotpot-arcade/worker-analytics-client';
import { StorePage } from '../pages/store-page';
import { ViewportSizeProvider } from '@tofu/viewport';
import { AppFrame } from '../components/app-frame';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { FanforgeHome } from '../pages/fanforge-home';
import { AlertOverlay } from '../pages/alert-overlay';
import { AlertOverlayTest } from '../pages/alert-overlay-test';
import { MafiaOnly } from '../pages/mafia-only';

// Build variant. 'fanforge' (default) is the full arcade; 'mafia' boots straight
// into the standalone Mafia Discord activity with no arcade shell.
const APP_VARIANT = import.meta.env.VITE_APP_VARIANT ?? 'fanforge';

export function App() {
  const auth = useAuth((context) => context);

  useEffect(() => {
    if (auth.state === 'authenticated') {
      Sentry.setUser({
        id: auth.jwtPayload.sub,
        username: auth.jwtPayload.username,
        discord_user_id: auth.jwtPayload.amr === 'discord' ? auth.jwtPayload.discordId : 'web',
      });
      Sentry.getCurrentScope().setTag(
        'discord_guild_id',
        auth.jwtPayload.amr === 'discord' ? (auth.jwtPayload.guildId ?? '') : 'web'
      );
    }
  }, [auth]);

  switch (auth.state) {
    case 'authenticated': {
      // Standalone Mafia activity: boot straight into the game, no arcade shell.
      // A catch-all route is kept so Mafia's internal navigate('/') (exit / back
      // buttons) resets to the game instead of white-screening.
      if (APP_VARIANT === 'mafia') {
        return (
          <BrowserRouter>
            <ViewportSizeProvider>
              <Routes>
                <Route path="*" element={<MafiaOnly />} />
              </Routes>
            </ViewportSizeProvider>
          </BrowserRouter>
        );
      }

      return (
        <>
          <BrowserRouter>
            <ViewportSizeProvider>
              <RecognitionDoseProvider>
                <AppFrame>
                  <Routes>
                    <Route path="/" element={<FanforgeHome />} />
                    <Route path="/store" element={<StorePage />} />
                    <Route path="/:id/*" element={<GamePage />} />
                    <Route path="/alert-overlay/:partnerId" element={<AlertOverlay />} />
                    <Route path="/alert-overlay/:partnerId/test" element={<AlertOverlayTest />} />
                  </Routes>
                </AppFrame>
              </RecognitionDoseProvider>
            </ViewportSizeProvider>
          </BrowserRouter>
        </>
      );
    }
    case 'unauthenticated':
      return <UnauthenticatedPage />;
    default:
      return <LoadingPage variant={APP_VARIANT === 'mafia' ? 'mafia' : 'fanforge'} />;
  }
}
